import React, { useState } from 'react';
import './App.css';

const emailCharCodes = [
  106, 100, 111, 109, 49, 56, 50, 52, 64, 103, 109, 97, 105, 108, 46, 99, 111, 109,
];

const revealEmail = () => String.fromCharCode(...emailCharCodes);

const content = {
  en: {
    langLabel: 'Language',
    langOptions: { en: 'English', es: 'Español' },
    kicker: 'Research profile',
    name: 'Juan David Peña Melo',
    role: 'Blockchain Researcher & Computer Scientist',
    summary:
      'Ph.D. in Computer Science from the National Institute of Astrophysics, Optics, and Electronics, specializing in distributed systems, logical time, cybersecurity, and blockchain. My professional background integrates technological and scientific work, enabling projects with short-, medium-, and long-term impact.',
    profileStats: [
      { label: 'Degree', value: 'PhD' },
      { label: 'Primary field', value: 'Blockchain research' },
      { label: 'Secondary field', value: 'Distributed systems' },
      { label: 'Location', value: 'Colombia / Mexico' },
    ],
    profileLinks: [
      { label: 'ORCID', href: 'https://orcid.org/0000-0002-1946-3238' },
      {
        label: 'CvLAC',
        href: 'https://scienti.minciencias.gov.co/cvlac/visualizador/generarCurriculoCv.do?cod_rh=0002036002',
      },
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/in/juan-david-pe%C3%B1a-melo-phd-367ba7296/',
      },
    ],
    ascii: [
      'PROFILE',
      'PHD / COMPUTER SCIENCE',
      'BLOCKCHAIN / CYBERSECURITY / DISTRIBUTED SYSTEMS',
      'PUBLICATIONS / REVIEW / TALKS / TECHNOLOGY TRANSFER',
    ],
    sections: {
      technicalKicker: 'Skills',
      technicalTitle: 'Technical skills',
      softKicker: 'Skills',
      softTitle: 'Soft skills',
      researchKicker: 'Research',
      researchTitle: 'Research experience and publications',
      workKicker: 'Work',
      workTitle: 'Work experience',
      reviewKicker: 'Review',
      reviewTitle: 'Reviewer activity',
      talksKicker: 'Talks',
      talksTitle: 'Talks and workshops',
      educationKicker: 'Education',
      educationTitle: 'Academic formation',
      recognitionsKicker: 'Recognitions',
      recognitionsTitle: 'Awards and scholarships',
      trainingKicker: 'Training',
      trainingTitle: 'Additional training',
      galleryKicker: 'Gallery',
      galleryTitle: 'Travel, talks, and professional moments',
      contactKicker: 'Contact',
      contactTitle: 'Direct contact',
    },
    technicalSkills: [
      'Computational complexity theory',
      'Distributed systems and logical time',
      'Cybersecurity in blockchain contexts',
      'Blockchain traceability and smart-contract applications',
      'C++, Python, JavaScript, Android Studio, Kotlin',
      'React Native, Matlab, OpenCV, and data-driven software development',
    ],
    softSkills: [
      'Leadership and team coordination in multidisciplinary projects',
      'Clear communication of technical ideas to diverse audiences',
      'Adaptability in research and technology environments',
      'Critical thinking and problem solving',
      'Results orientation across short-, medium-, and long-term initiatives',
      'Interdisciplinary collaboration in technology and research projects',
    ],
    researchItems: [
      {
        title: 'My two Bitcoins? Implementation of Double-Spending on Fast Bitcoin Payments',
        venue: 'Congreso Internacional CORE 2022',
        status: 'Sep 2022 · Published',
      },
      {
        title: 'Patent NC2022/0012844',
        venue:
          'Indirect procedure for measuring CO2 capture capacity using artificial intelligence and blockchain through drones in cocoa crops. Superintendence of Industry and Commerce (Colombia)',
        status: 'Filing date: September 8, 2022',
      },
      {
        title: 'Bitcoin Transactions Types and Their Impact on Storage Scalability',
        venue:
          '31st IEEE International Conference on Enabling Technologies: Infrastructure for Collaborative Enterprises (WETICE 2023)',
        status: 'Dec 2023 · Published',
      },
      {
        title: 'Replication data for: Unlocking blockchain UTXO transactional patterns',
        venue: 'Harvard Dataverse',
        status: 'Feb 2024 · Published',
      },
      {
        title:
          'Unlocking Blockchain UTXO Transactional Patterns and Their Effect on Storage and Throughput Trade-Offs',
        venue: 'Computers, MDPI',
        status: 'Jun 2024 · Published',
      },
      {
        title: 'DiFastBit: Transaction Differentiation Scheme to Avoid Double Spending for Fast Bitcoin Payments',
        venue: 'Mathematics, MDPI',
        status: 'Aug 2024 · Published',
      },
      {
        title: 'Analyzing Throughput and Storage Scalability in Bitcoin Transaction Types Over Time',
        venue: 'Peer-to-Peer Networking and Applications, Springer',
        status: 'Jan 2025 · Under review',
      },
    ],
    reviewerItems: [
      {
        title: 'Reviewer for SN Computer Science',
        venue: 'Journal, Springer Nature · ISSN: 2661-8907',
        status: '1 review · 2025 · Role: Reviewer',
      },
      {
        title: 'Reviewer for Computer Networks',
        venue: 'Journal, Computer Networks · ISSN: 1389-1286',
        status: '4 reviews · 2023–2026 · Role: Reviewer',
      },
      {
        title: 'Reviewer for PLOS ONE',
        venue: 'Journal, PLOS ONE · ISSN: 1932-6203',
        status: '1 review · 2025 · Role: Reviewer',
      },
      {
        title: 'Reviewer for Journal of Network and Systems Management',
        venue: 'Journal, Springer Nature · 2026',
        status: '1 review · 2026 · Role: Reviewer',
      },
    ],
    workItems: [
      {
        title: 'Chief Technology Infrastructure',
        venue: 'COINS TECH | Aug 2014 - May 2018',
        detail:
          'Oversaw technological development projects focused on computer vision, access systems, and database management, including a license plate recognition system deployed for Universidad Santo Tomas de Bucaramanga.',
      },
      {
        title: 'Developer of an Architecture for Risk Analysis and Detection in Chronic Degenerative Diseases',
        venue: 'Conacyt Public Centers Consortium (CIMAT, CICESE, INAOE) | Aug 2020 - Dec 2020',
        detail:
          'Developed an Android application with React Native for continuous monitoring and a REST API for early risk detection through real-time data collection.',
      },
      {
        title: 'Developer of The Kiss Genesis Project NFT',
        venue: 'Vertigo Graffiti and Inmutable Lab | Nov 2021 - Dec 2021',
        detail:
          'Led the implementation of the NFT project associated with El Beso de los Invisibles in Bogota, coordinating stakeholders around tokenized digital assets.',
      },
      {
        title: 'Developer of Strax Dashboard Transactions Traceability',
        venue: 'The Stable Europa Validator | May 2022 - Aug 2022',
        detail:
          'Designed and maintained traceability functionality for blockchain transaction tracking and analysis in the Strax Dashboard.',
      },
      {
        title: 'Development Project Leader at CONEXALAB',
        venue: 'CONEXALAB SAS BIC | Nov 2021 - Feb 2024',
        detail:
          'Contributed to MAMUS and i-CO2, including NFT certificate generation for students and a blockchain- and AI-enabled traceability initiative for agricultural impact tracking.',
      },
      {
        title: 'Project Leader at AGROIMPULSO',
        venue: 'Chamber of Commerce of Armenia | Nov 2024 - Feb 2025',
        detail:
          'Led the methodological design of a cold-climate crop laboratory with blockchain-based funding, smart contracts, and IoT monitoring for traceability and transparency.',
      },
    ],
    education: [
      'Ph.D. in Computer Science - INAOE (2021 - 2024)',
      'M.Sc. in Computer Science - INAOE (2018 - 2020)',
      'Electronic Engineering - Universidad de Investigacion y Desarrollo (2017)',
    ],
    recognitions: [
      'CONACYT Ph.D. scholarship (2020 - 2024)',
      "Winner of INN'OVATE, Colombian Petroleum Institute (March 2022)",
      'CONACYT MSc. scholarship (2018 - 2020)',
      'Real World Crypto (RWC) 2025 Stipend - IACR (February 2025)',
    ],
    training: [
      'Workshop: Strengthening Your IoT Nodes with Microchip Solutions - INAOE / Microside (Oct 15 - 17, 2018)',
      'Quantum Computing: Generalities, Applications, and Programming - INAOE (Jan 30, 2024 - Feb 8, 2024)',
    ],
    talks: [
      {
        title: 'Decentralization and Blockchain: A Step Towards Decentralized Applications',
        venue: '5th Edition of the Autumn School on Distributed Systems and Next-Generation Networks, INAOE',
        status: 'San Andres Cholula, Mexico · Oct 23 - 25, 2023',
      },
      {
        title: 'Bitcoin Transactions Types and Their Impact on Storage Scalability',
        venue:
          '31st IEEE International Conference on Enabling Technologies: Infrastructure for Collaborative Enterprises (WETICE-2023)',
        status: 'Paris, France · Dec 14 - 16, 2023',
      },
      {
        title: 'Introduccion a Blockchain y Contratos Inteligentes',
        venue: '7a Escuela de Otono de Sistemas Distribuidos y Redes de Proxima Generacion / RCI 5',
        status: 'INAOE · 2025',
      },
      {
        title: 'Taller de Trazabilidad Inteligente con LLMs',
        venue: 'Gobierno de Tlaxcala',
        status: '2026',
      },
    ],
    gallery: [
      {
        src: '/gallery/IMG-20221105-WA0070.jpg',
        title: 'Research presentation on applied methodologies',
        caption: 'An early-stage talk setting focused on technical work and project communication.',
      },
      {
        src: '/gallery/IMG-20221105-WA0077.jpg',
        title: 'Blockchain and innovation presentation',
        caption: 'Public-facing presentation work in a technology and entrepreneurship environment.',
      },
      {
        src: '/gallery/IMG-20231024-WA0009.jpg',
        title: 'Autumn School talk at INAOE',
        caption: 'Presentation during the Autumn School on distributed systems and next-generation networks.',
      },
      {
        src: '/gallery/IMG-20231024-WA0011.jpg',
        title: 'Decentralization and blockchain session',
        caption: 'A speaking moment centered on decentralized applications and blockchain concepts.',
      },
      {
        src: '/gallery/IMG_20250326_180059.jpg',
        title: 'Sofia city center',
        caption: 'Professional travel in Sofia, Bulgaria, during Real World Crypto 2025.',
      },
      {
        src: '/gallery/IMG_20250326_181843.jpg',
        title: 'National Assembly area in Sofia',
        caption: 'Urban and institutional landscape around the conference visit in Bulgaria.',
      },
      {
        src: '/gallery/IMG_20250326_181923.jpg',
        title: 'Sofia landmarks and conference travel',
        caption: 'A visual record of international mobility linked to academic participation.',
      },
      {
        src: '/gallery/IMG_20250326_184157.jpg',
        title: 'Ivan Vazov National Theatre',
        caption: 'One of the cultural landmarks photographed during the Sofia research trip.',
      },
      {
        src: '/gallery/IMG_20250327_171100.jpg',
        title: 'Alexander Nevsky Cathedral',
        caption: 'A representative moment from the Bulgaria conference trip and city exploration.',
      },
      {
        src: '/gallery/IMG_20250327_171102.jpg',
        title: 'Cathedral square in Sofia',
        caption: 'Another scene from the same academic trip, connecting travel and conference life.',
      },
      {
        src: '/gallery/IMG_20250327_172836.jpg',
        title: 'Conference travel in Bulgaria',
        caption: 'Images from the surroundings of Real World Crypto 2025 in Sofia.',
      },
      {
        src: '/gallery/IMG_20250327_172840.jpg',
        title: 'Historic district near the cathedral',
        caption: 'A final street-level view from the Bulgaria research and conference visit.',
      },
      {
        src: '/gallery/IMG_20251113_134113.jpg',
        title: 'Doctoral graduation portrait',
        caption: 'A formal milestone marking the completion of the doctoral stage.',
      },
      {
        src: '/gallery/IMG_20251113_140232.jpg',
        title: 'Graduation day at INAOE',
        caption: 'Academic closure of the PhD journey in Computational Sciences.',
      },
      {
        src: '/gallery/IMG_20251126_140343.jpg',
        title: 'Autumn School welcome banner',
        caption: 'Presence at a specialized academic event on distributed systems and cybersecurity.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.29.jpeg',
        title: 'Real World Crypto 2025 audience moment',
        caption: 'Inside the conference environment with fellow attendees in Sofia, Bulgaria.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.30.jpeg',
        title: 'Conference networking',
        caption: 'An informal conference moment with participants during Real World Crypto 2025.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.32.jpeg',
        title: 'Real World Crypto 2025 venue',
        caption: 'At the entrance signage for the conference in Sofia, Bulgaria.',
      },
      {
        src: '/gallery/event-fuego-group.jpeg',
        title: 'Group photo at Fuego NFT Fest',
        caption: 'Community and ecosystem moment around blockchain, NFTs, and public-facing tech events.',
      },
      {
        src: '/gallery/event-ethichub-demo-1.jpeg',
        title: 'EthicHub technical demo session',
        caption: 'Presenting a blockchain-related platform and transaction workflow in a live event setting.',
      },
      {
        src: '/gallery/event-ethichub-demo-2.jpeg',
        title: 'Discussion during product demonstration',
        caption: 'An event-space conversation around blockchain tooling, interfaces, and practical use cases.',
      },
    ],
    contact: {
      intro:
        'For research collaboration, academic invitations, peer-review, talks, or consulting conversations, the email is intentionally human-readable first and machine-readable only after interaction.',
      maskedEmail: 'jdom1824 [at] gmail [dot] com',
      revealLabel: 'Reveal email',
      hiddenLabel: 'Protected from basic scraping',
      copyLabel: 'Copy email',
      copiedLabel: 'Copied',
      mailLabel: 'Open mail app',
    },
  },
  es: {
    langLabel: 'Idioma',
    langOptions: { en: 'English', es: 'Español' },
    kicker: 'Perfil de investigación',
    name: 'Juan David Peña Melo',
    role: 'Investigador en Blockchain y Científico Computacional',
    summary:
      'Doctor en Ciencias Computacionales por el Instituto Nacional de Astrofísica, Óptica y Electrónica, con especialización en sistemas distribuidos, tiempo lógico, ciberseguridad y blockchain. Mi trayectoria integra trabajo científico y tecnológico para impulsar proyectos con impacto a corto, mediano y largo plazo.',
    profileStats: [
      { label: 'Grado', value: 'Doctorado' },
      { label: 'Campo principal', value: 'Investigación en blockchain' },
      { label: 'Campo secundario', value: 'Sistemas distribuidos' },
      { label: 'Ubicación', value: 'Colombia / México' },
    ],
    profileLinks: [
      { label: 'ORCID', href: 'https://orcid.org/0000-0002-1946-3238' },
      {
        label: 'CvLAC',
        href: 'https://scienti.minciencias.gov.co/cvlac/visualizador/generarCurriculoCv.do?cod_rh=0002036002',
      },
      {
        label: 'LinkedIn',
        href: 'https://www.linkedin.com/in/juan-david-pe%C3%B1a-melo-phd-367ba7296/',
      },
    ],
    ascii: [
      'PERFIL',
      'DOCTORADO / CIENCIAS COMPUTACIONALES',
      'BLOCKCHAIN / CIBERSEGURIDAD / SISTEMAS DISTRIBUIDOS',
      'PUBLICACIONES / REVISIÓN / PONENCIAS / TRANSFERENCIA',
    ],
    sections: {
      technicalKicker: 'Habilidades',
      technicalTitle: 'Habilidades técnicas',
      softKicker: 'Habilidades',
      softTitle: 'Habilidades blandas',
      researchKicker: 'Investigación',
      researchTitle: 'Experiencia investigativa y publicaciones',
      workKicker: 'Trabajo',
      workTitle: 'Experiencia laboral',
      reviewKicker: 'Revisión',
      reviewTitle: 'Actividad como revisor',
      talksKicker: 'Ponencias',
      talksTitle: 'Ponencias y talleres',
      educationKicker: 'Formación',
      educationTitle: 'Formación académica',
      recognitionsKicker: 'Reconocimientos',
      recognitionsTitle: 'Premios y becas',
      trainingKicker: 'Capacitación',
      trainingTitle: 'Formación adicional',
      galleryKicker: 'Galería',
      galleryTitle: 'Viajes, ponencias y momentos profesionales',
      contactKicker: 'Contacto',
      contactTitle: 'Contacto directo',
    },
    technicalSkills: [
      'Teoría de complejidad computacional',
      'Sistemas distribuidos y tiempo lógico',
      'Ciberseguridad en contextos blockchain',
      'Trazabilidad blockchain y aplicaciones con contratos inteligentes',
      'C++, Python, JavaScript, Android Studio y Kotlin',
      'React Native, Matlab, OpenCV y desarrollo de software basado en datos',
    ],
    softSkills: [
      'Liderazgo y coordinación de equipos multidisciplinarios',
      'Comunicación clara de ideas técnicas a diferentes audiencias',
      'Adaptabilidad en entornos de investigación y tecnología',
      'Pensamiento crítico y resolución de problemas',
      'Orientación a resultados en iniciativas de corto, mediano y largo plazo',
      'Colaboración interdisciplinaria en proyectos tecnológicos y científicos',
    ],
    researchItems: [
      {
        title: 'My two Bitcoins? Implementation of Double-Spending on Fast Bitcoin Payments',
        venue: 'Congreso Internacional CORE 2022',
        status: 'Sep 2022 · Publicado',
      },
      {
        title: 'Patente NC2022/0012844',
        venue:
          'Procedimiento indirecto para medir la capacidad de captura de CO2 mediante inteligencia artificial y blockchain con drones en cultivos de cacao. Superintendencia de Industria y Comercio (Colombia)',
        status: 'Radicación: 8 de septiembre de 2022',
      },
      {
        title: 'Bitcoin Transactions Types and Their Impact on Storage Scalability',
        venue:
          '31st IEEE International Conference on Enabling Technologies: Infrastructure for Collaborative Enterprises (WETICE 2023)',
        status: 'Dic 2023 · Publicado',
      },
      {
        title: 'Replication data for: Unlocking blockchain UTXO transactional patterns',
        venue: 'Harvard Dataverse',
        status: 'Feb 2024 · Publicado',
      },
      {
        title:
          'Unlocking Blockchain UTXO Transactional Patterns and Their Effect on Storage and Throughput Trade-Offs',
        venue: 'Computers, MDPI',
        status: 'Jun 2024 · Publicado',
      },
      {
        title: 'DiFastBit: Transaction Differentiation Scheme to Avoid Double Spending for Fast Bitcoin Payments',
        venue: 'Mathematics, MDPI',
        status: 'Ago 2024 · Publicado',
      },
      {
        title: 'Analyzing Throughput and Storage Scalability in Bitcoin Transaction Types Over Time',
        venue: 'Peer-to-Peer Networking and Applications, Springer',
        status: 'Ene 2025 · En revisión',
      },
    ],
    reviewerItems: [
      {
        title: 'Revisor de SN Computer Science',
        venue: 'Revista, Springer Nature · ISSN: 2661-8907',
        status: '1 revisión · 2025 · Rol: Revisor',
      },
      {
        title: 'Revisor de Computer Networks',
        venue: 'Revista, Computer Networks · ISSN: 1389-1286',
        status: '4 revisiones · 2023–2026 · Rol: Revisor',
      },
      {
        title: 'Revisor de PLOS ONE',
        venue: 'Revista, PLOS ONE · ISSN: 1932-6203',
        status: '1 revisión · 2025 · Rol: Revisor',
      },
      {
        title: 'Revisor de Journal of Network and Systems Management',
        venue: 'Revista, Springer Nature · 2026',
        status: '1 revisión · 2026 · Rol: Revisor',
      },
    ],
    workItems: [
      {
        title: 'Chief Technology Infrastructure',
        venue: 'COINS TECH | Ago 2014 - May 2018',
        detail:
          'Supervisé proyectos de desarrollo tecnológico enfocados en visión por computador, sistemas de acceso y gestión de bases de datos, incluyendo un sistema de reconocimiento de placas desplegado para la Universidad Santo Tomás de Bucaramanga.',
      },
      {
        title: 'Desarrollador de una arquitectura para análisis y detección de riesgo en enfermedades crónico degenerativas',
        venue: 'Consorcio de Centros Públicos Conacyt (CIMAT, CICESE, INAOE) | Ago 2020 - Dic 2020',
        detail:
          'Desarrollé una aplicación Android con React Native para monitoreo continuo y una API REST para detección temprana de riesgo mediante recolección de datos en tiempo real.',
      },
      {
        title: 'Desarrollador del proyecto NFT The Kiss Genesis',
        venue: 'Vertigo Graffiti e Inmutable Lab | Nov 2021 - Dic 2021',
        detail:
          'Lideré la implementación del proyecto NFT asociado a El Beso de los Invisibles en Bogotá, coordinando actores alrededor de activos digitales tokenizados.',
      },
      {
        title: 'Desarrollador de trazabilidad de transacciones para Strax Dashboard',
        venue: 'The Stable Europa Validator | May 2022 - Ago 2022',
        detail:
          'Diseñé y mantuve funcionalidades de trazabilidad para seguimiento y análisis de transacciones blockchain dentro de Strax Dashboard.',
      },
      {
        title: 'Líder de proyectos de desarrollo en CONEXALAB',
        venue: 'CONEXALAB SAS BIC | Nov 2021 - Feb 2024',
        detail:
          'Contribuí a MAMUS e i-CO2, incluyendo generación de certificados NFT para estudiantes y una iniciativa de trazabilidad con blockchain e IA para seguimiento de impacto agrícola.',
      },
      {
        title: 'Líder de proyecto en AGROIMPULSO',
        venue: 'Cámara de Comercio de Armenia | Nov 2024 - Feb 2025',
        detail:
          'Lideré el diseño metodológico de un laboratorio de cultivos de clima frío con financiación en blockchain, contratos inteligentes y monitoreo IoT para trazabilidad y transparencia.',
      },
    ],
    education: [
      'Doctorado en Ciencias Computacionales - INAOE (2021 - 2024)',
      'Maestría en Ciencias Computacionales - INAOE (2018 - 2020)',
      'Ingeniería Electrónica - Universidad de Investigación y Desarrollo (2017)',
    ],
    recognitions: [
      'Beca de doctorado CONACYT (2020 - 2024)',
      "Ganador de INN'OVATE, Instituto Colombiano del Petróleo (marzo de 2022)",
      'Beca de maestría CONACYT (2018 - 2020)',
      'Stipend Real World Crypto (RWC) 2025 - IACR (febrero de 2025)',
    ],
    training: [
      'Taller: Strengthening Your IoT Nodes with Microchip Solutions - INAOE / Microside (15 - 17 oct 2018)',
      'Quantum Computing: Generalities, Applications, and Programming - INAOE (30 ene 2024 - 8 feb 2024)',
    ],
    talks: [
      {
        title: 'Decentralization and Blockchain: A Step Towards Decentralized Applications',
        venue: '5a Escuela de Otoño de Sistemas Distribuidos y Redes de Nueva Generación, INAOE',
        status: 'San Andrés Cholula, México · 23 - 25 oct 2023',
      },
      {
        title: 'Bitcoin Transactions Types and Their Impact on Storage Scalability',
        venue:
          '31st IEEE International Conference on Enabling Technologies: Infrastructure for Collaborative Enterprises (WETICE-2023)',
        status: 'París, Francia · 14 - 16 dic 2023',
      },
      {
        title: 'Introducción a Blockchain y Contratos Inteligentes',
        venue: '7a Escuela de Otoño de Sistemas Distribuidos y Redes de Próxima Generación / RCI 5',
        status: 'INAOE · 2025',
      },
      {
        title: 'Taller de Trazabilidad Inteligente con LLMs',
        venue: 'Gobierno de Tlaxcala',
        status: '2026',
      },
    ],
    gallery: [
      {
        src: '/gallery/IMG-20221105-WA0070.jpg',
        title: 'Presentación de investigación sobre metodologías aplicadas',
        caption: 'Un entorno temprano de charla técnica y comunicación de proyectos.',
      },
      {
        src: '/gallery/IMG-20221105-WA0077.jpg',
        title: 'Presentación sobre blockchain e innovación',
        caption: 'Trabajo de divulgación en un entorno de tecnología y emprendimiento.',
      },
      {
        src: '/gallery/IMG-20231024-WA0009.jpg',
        title: 'Ponencia en la Escuela de Otoño del INAOE',
        caption: 'Presentación durante la Escuela de Otoño en sistemas distribuidos y redes de nueva generación.',
      },
      {
        src: '/gallery/IMG-20231024-WA0011.jpg',
        title: 'Sesión sobre descentralización y blockchain',
        caption: 'Un momento de charla centrado en aplicaciones descentralizadas y conceptos blockchain.',
      },
      {
        src: '/gallery/IMG_20250326_180059.jpg',
        title: 'Centro de Sofía',
        caption: 'Viaje profesional en Sofía, Bulgaria, durante Real World Crypto 2025.',
      },
      {
        src: '/gallery/IMG_20250326_181843.jpg',
        title: 'Zona de la Asamblea Nacional en Sofía',
        caption: 'Paisaje urbano e institucional alrededor de la visita académica en Bulgaria.',
      },
      {
        src: '/gallery/IMG_20250326_181923.jpg',
        title: 'Referentes urbanos durante el viaje académico',
        caption: 'Registro visual de la movilidad internacional ligada a participación científica.',
      },
      {
        src: '/gallery/IMG_20250326_184157.jpg',
        title: 'Teatro Nacional Ivan Vazov',
        caption: 'Uno de los referentes culturales fotografiados durante el viaje a Sofía.',
      },
      {
        src: '/gallery/IMG_20250327_171100.jpg',
        title: 'Catedral Alexander Nevsky',
        caption: 'Un momento representativo del viaje de conferencia y exploración urbana en Bulgaria.',
      },
      {
        src: '/gallery/IMG_20250327_171102.jpg',
        title: 'Plaza de la catedral en Sofía',
        caption: 'Otra escena del mismo viaje académico que conecta ciudad y vida de conferencia.',
      },
      {
        src: '/gallery/IMG_20250327_172836.jpg',
        title: 'Recorrido de conferencia en Bulgaria',
        caption: 'Imágenes del entorno de Real World Crypto 2025 en Sofía.',
      },
      {
        src: '/gallery/IMG_20250327_172840.jpg',
        title: 'Sector histórico cerca de la catedral',
        caption: 'Una vista final a nivel de calle del viaje de investigación y conferencia.',
      },
      {
        src: '/gallery/IMG_20251113_134113.jpg',
        title: 'Retrato de graduación doctoral',
        caption: 'Un hito formal que marca el cierre de la etapa doctoral.',
      },
      {
        src: '/gallery/IMG_20251113_140232.jpg',
        title: 'Día de grado en el INAOE',
        caption: 'Cierre académico del recorrido doctoral en Ciencias Computacionales.',
      },
      {
        src: '/gallery/IMG_20251126_140343.jpg',
        title: 'Letrero de bienvenida de la Escuela de Otoño',
        caption: 'Presencia en un evento académico especializado en sistemas distribuidos y ciberseguridad.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.29.jpeg',
        title: 'Momento entre asistentes en Real World Crypto 2025',
        caption: 'Dentro del ambiente de conferencia con otros participantes en Sofía, Bulgaria.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.30.jpeg',
        title: 'Networking en conferencia',
        caption: 'Un momento informal con participantes durante Real World Crypto 2025.',
      },
      {
        src: '/gallery/whatsapp/WhatsApp Image 2026-04-10 at 22.50.32.jpeg',
        title: 'Acceso a Real World Crypto 2025',
        caption: 'Frente al cartel de entrada del congreso en Sofía, Bulgaria.',
      },
      {
        src: '/gallery/event-fuego-group.jpeg',
        title: 'Foto grupal en Fuego NFT Fest',
        caption: 'Un momento de comunidad y ecosistema alrededor de blockchain, NFTs y eventos tecnológicos abiertos.',
      },
      {
        src: '/gallery/event-ethichub-demo-1.jpeg',
        title: 'Sesión de demo técnica de EthicHub',
        caption: 'Presentación en vivo de una plataforma relacionada con blockchain y flujos de transacciones.',
      },
      {
        src: '/gallery/event-ethichub-demo-2.jpeg',
        title: 'Conversación durante una demostración de producto',
        caption: 'Intercambio en espacio de evento sobre herramientas blockchain, interfaces y casos de uso reales.',
      },
    ],
    contact: {
      intro:
        'Para colaboraciones de investigación, invitaciones académicas, revisión por pares, ponencias o conversaciones de consultoría, el correo se muestra primero en formato legible para humanos y solo se vuelve clicable tras la interacción.',
      maskedEmail: 'jdom1824 [at] gmail [dot] com',
      revealLabel: 'Revelar correo',
      hiddenLabel: 'Protegido contra scraping básico',
      copyLabel: 'Copiar correo',
      copiedLabel: 'Copiado',
      mailLabel: 'Abrir correo',
    },
  },
};

function SectionList({ items, showDetail = false }) {
  return (
    <div className="publication-table">
      {items.map((item) => (
        <article className="publication-row" key={`${item.title}-${item.venue}`}>
          <div className="publication-main">
            <h3>{item.title}</h3>
            <p>{item.venue}</p>
            {showDetail ? <span>{item.detail}</span> : null}
          </div>
          <span className="publication-status">{item.status}</span>
        </article>
      ))}
    </div>
  );
}

function App() {
  const [lang, setLang] = useState('en');
  const [emailVisible, setEmailVisible] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const t = content[lang];
  const realEmail = revealEmail();

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(realEmail);
      setEmailCopied(true);
      window.setTimeout(() => setEmailCopied(false), 1800);
    } catch (error) {
      setEmailCopied(false);
    }
  };

  return (
    <div className="page">
      <main className="cv">
        <div className="language-bar">
          <p>{t.langLabel}</p>
          <div className="language-switch" role="tablist" aria-label={t.langLabel}>
            <button
              type="button"
              className={lang === 'en' ? 'active' : ''}
              onClick={() => setLang('en')}
            >
              {content.en.langOptions.en}
            </button>
            <button
              type="button"
              className={lang === 'es' ? 'active' : ''}
              onClick={() => setLang('es')}
            >
              {content.es.langOptions.es}
            </button>
          </div>
        </div>

        <section className="masthead">
          <div className="masthead-left">
            <p className="kicker">{t.kicker}</p>
            <h1>{t.name}</h1>
            <p className="summary">{t.role}</p>
            <p className="summary detail-summary">{t.summary}</p>
            <div className="profile-links">
              {t.profileLinks.map((link) => (
                <a href={link.href} target="_blank" rel="noreferrer" key={link.label}>
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          <div className="masthead-right">
            <div className="ascii-block">
              {t.ascii.map((line) => (
                <span key={line}>{line}</span>
              ))}
            </div>
          </div>
        </section>

        <section className="meta-grid">
          {t.profileStats.map((item) => (
            <article className="meta-card" key={item.label}>
              <p>{item.label}</p>
              <h2>{item.value}</h2>
            </article>
          ))}
        </section>

        <section className="content-grid">
          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.technicalKicker}</p>
              <h2>{t.sections.technicalTitle}</h2>
            </div>
            <ul className="list">
              {t.technicalSkills.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.softKicker}</p>
              <h2>{t.sections.softTitle}</h2>
            </div>
            <ul className="list">
              {t.softSkills.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="panel wide">
            <div className="panel-head">
              <p>{t.sections.researchKicker}</p>
              <h2>{t.sections.researchTitle}</h2>
            </div>
            <SectionList items={t.researchItems} />
          </section>

          <section className="panel wide">
            <div className="panel-head">
              <p>{t.sections.workKicker}</p>
              <h2>{t.sections.workTitle}</h2>
            </div>
            <SectionList items={t.workItems} showDetail />
          </section>

          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.reviewKicker}</p>
              <h2>{t.sections.reviewTitle}</h2>
            </div>
            <SectionList items={t.reviewerItems} />
          </section>

          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.talksKicker}</p>
              <h2>{t.sections.talksTitle}</h2>
            </div>
            <SectionList items={t.talks} />
          </section>

          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.educationKicker}</p>
              <h2>{t.sections.educationTitle}</h2>
            </div>
            <ul className="list">
              {t.education.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="panel">
            <div className="panel-head">
              <p>{t.sections.recognitionsKicker}</p>
              <h2>{t.sections.recognitionsTitle}</h2>
            </div>
            <ul className="list">
              {t.recognitions.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="panel wide">
            <div className="panel-head">
              <p>{t.sections.trainingKicker}</p>
              <h2>{t.sections.trainingTitle}</h2>
            </div>
            <ul className="list">
              {t.training.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="panel wide contact-panel">
            <div className="panel-head">
              <p>{t.sections.contactKicker}</p>
              <h2>{t.sections.contactTitle}</h2>
            </div>
            <p className="contact-intro">{t.contact.intro}</p>
            <div className="contact-card">
              <p className="contact-code">
                {emailVisible ? realEmail : t.contact.maskedEmail}
              </p>
              <p className="contact-note">
                {emailVisible ? t.contact.mailLabel : t.contact.hiddenLabel}
              </p>
              <div className="contact-actions">
                <button type="button" onClick={() => setEmailVisible(true)}>
                  {t.contact.revealLabel}
                </button>
                <button type="button" onClick={handleCopyEmail}>
                  {emailCopied ? t.contact.copiedLabel : t.contact.copyLabel}
                </button>
                {emailVisible ? (
                  <a href={`mailto:${realEmail}`}>{t.contact.mailLabel}</a>
                ) : null}
              </div>
            </div>
          </section>

          <section className="panel wide">
            <div className="panel-head">
              <p>{t.sections.galleryKicker}</p>
              <h2>{t.sections.galleryTitle}</h2>
            </div>
            <div className="gallery-grid">
              {t.gallery.map((item) => (
                <figure className="gallery-card" key={item.src}>
                  <img src={item.src} alt={item.title} />
                  <figcaption>
                    <h3>{item.title}</h3>
                    <p>{item.caption}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}

export default App;
